import { Box, FormControl, FormErrorMessage, FormLabel, IconButton, Input, InputGroup, InputRightElement, Tag } from '@chakra-ui/react';
import React, { FC, ReactNode, useEffect, useRef, useState } from 'react';
import { FaAngleDown } from 'react-icons/fa6';

import { useAlert } from '../../hooks/use-alert';
import { Dropdown } from './dropdown';
import { FieldComponentProps } from './types';

type SelectFieldProps = FieldComponentProps<EntityBase[]> & {
  options?: EntityBase[];
  name?: string;
  field?: string;
  onChangeValue?: (id: string) => void;
  onClickItem?: (item: EntityBase) => void;
  onFetchOptions?: (name: string) => Promise<EntityBase[]>;
  loading?: boolean;
  clearable?: boolean;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  customBodyRender?: (value: EntityBase) => ReactNode;
};

export const MultiSelectField: FC<SelectFieldProps> = (props) => {
  const {
    id,
    name,
    label,
    disabled,
    value,
    field = 'name',
    onChange,
    onClickItem,
    onFetchOptions,
    loading,
    error,
    helperText,
    required,
    size = 'md',
    customBodyRender,
    readOnly = false,
  } = props;
  const [open, setOpen] = useState(false);
  const fieldRef = useRef<HTMLDivElement>(null);
  const [options, setOptions] = useState<EntityBase[]>(
    props.options || [...(value ? [value] : [])]
  );
  const [isFetchDone, setIsFetchDone] = useState<boolean>(false);

  useEffect(() => {
    // if (options.length === 0)
    setOptions(props.options || []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.options]);

  useEffect(() => {
    if (options.length === 0 && props.value) {setOptions([props.value]);}
  }, [options.length, props.value]);

  const alert = useAlert();

  const onFocus = () => {
    !disabled && !readOnly && setOpen(true);
    if (!isFetchDone) {
      try {
        onFetchOptions && onFetchOptions(name).then(setOptions);
        setIsFetchDone(true);
      } catch (e) {
        console.log(e);
        alert.error('Ошибка при получении списка значений');
      }
    }
  };

  const handleMouseDown = () => {
    !disabled && !readOnly && setOpen(!open);

    if (!isFetchDone) {
      try {
        onFetchOptions && onFetchOptions(name).then(setOptions);
        setIsFetchDone(true);
      } catch (e) {
        console.log(e);
        alert.error('Ошибка при получении списка значений');
      }
    }
  };

  const handleClickOutside = () => {
    document.removeEventListener('mousedown', handleClickOutside);
    setOpen(false);
  };

  const handleMouseLeave = () => {
    document.addEventListener('mousedown', handleClickOutside);
  };

  const handleMouseEnter = () => {
    document.removeEventListener('mousedown', handleClickOutside);
  };

  const isSelected = (option: EntityBase) => {
    return value?.some(
      (item) =>
        option?.id === item?.id,
    );
  };

  const handleOptionSelect = (option: EntityBase) => {
    setOpen(false);
    onChange?.([...value, option]);
  };

  const handleOptionUnselect = (option: EntityBase) => {
    !disabled && onChange?.(
        value?.filter(
          (item) =>
            option.id !== item.id,
        )
      );
  };

  return (<InputGroup size={size}>
    <FormControl 
      variant="floating" 
      id={id} 
      isRequired={required}
      isInvalid={error}
      isDisabled={disabled}
      ref={fieldRef}
      onMouseLeave={handleMouseLeave}
      onMouseEnter={handleMouseEnter}
    >
      <InputRightElement pr={0}>
        <IconButton icon={<FaAngleDown />} aria-label="clear" 
          backgroundColor="transparent" 
          borderRadius="50%"
          size="xs"
          onClick={handleMouseDown}
          transform={open ?  'rotate(180deg)' : 'rotate(0deg)'}
        />
      </InputRightElement>
      <Dropdown 
        fieldRef={fieldRef}
        open={open}
        setOpen={setOpen}
      >
        {loading && (
          <Box  mx={4} my={2} textAlign='center'>
            Загрузка...
          </Box>
        )}
        {!loading && options.length === 0 && <Box 
          mx={4} my={2} textAlign='center'
          textColor='gray.400'
        >Пусто</Box>}
        {options?.map((item, index) => {
          const selected = isSelected(item);
          return <Dropdown.Option
            key={`${item.id}_${index}`}
            selected={selected}
            onClick={() => {
              onClickItem?.(item);
              selected
                ? handleOptionUnselect(item)
                : handleOptionSelect(item);
            }}
          >
            {customBodyRender?.(item) || item?.[field]}
          </Dropdown.Option>;
        })}
      </Dropdown>
      <Box 
        borderRadius='var(--input-border-radius)'
        paddingInlineStart='var(--input-padding)'
        paddingInlineEnd='var(--input-padding)'
        paddingY='1'
        border='1px solid'
        w='100%'
        minH='var(--input-height)'
        display='flex'
        flexWrap='wrap'
        gap={1}
        onClick={handleMouseDown}
      >
        {value?.map((item) => <Tag key={item.id} >{item?.[field]}</Tag>)}
      </Box>
      <Input 
        position="absolute"
        opacity={0}
        placeholder={label}
        value={value.join(',')}
        contentEditable={false}
        onFocus={onFocus}
        onClick={() => setOpen(true)}
        size={size}
        isReadOnly
      />
      <FormLabel textColor="dark.300">{label}</FormLabel>
      {error && <FormErrorMessage>{helperText}</FormErrorMessage>}
    </FormControl>
  </InputGroup>);
};

export default MultiSelectField;


